import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Mail, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, setToken, getToken, formatApiError } from "../lib/api";

export default function AdminLogin() {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        document.title = "Admin · Möbus Karriere";
        if (getToken()) navigate("/admin/dashboard", { replace: true });
    }, [navigate]);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
            setToken(data.access_token);
            toast.success("Willkommen zurück.");
            navigate("/admin/dashboard");
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Anmeldung fehlgeschlagen.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-moebus-deep flex flex-col">
            <div className="container mx-auto px-4 md:px-8 py-6">
                <Link to="/" className="inline-flex items-center gap-3 text-white">
                    <img
                        src="https://customer-assets.emergentagent.com/job_moebus-karriere/artifacts/15h4op5x_moebus_logo_fuer_wwww_kl%20Final%20Done-2.svg"
                        alt="Möbus Umweltschutz GmbH"
                        className="h-14 w-auto object-contain"
                    />
                    <div>
                        <div className="font-heading font-bold text-base">Möbus Umweltschutz</div>
                        <div className="text-[10px] uppercase tracking-widest text-emerald-400">Admin · Karriere</div>
                    </div>
                </Link>
            </div>

            <div className="flex-1 flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-md">
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/10 text-emerald-400 mb-4">
                            <Lock size={22} />
                        </div>
                        <h1 className="font-heading font-bold text-3xl text-white">Admin-Login</h1>
                        <p className="text-sm text-slate-400 mt-2">
                            Bewerbungsverwaltung · Möbus Umweltschutz GmbH
                        </p>
                    </div>

                    <form
                        data-testid="admin-login-form"
                        onSubmit={submit}
                        className="bg-white rounded-lg p-7 md:p-8 shadow-2xl"
                    >
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-moebus-deep mb-2">E-Mail</label>
                            <div className="relative">
                                <Mail
                                    size={16}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                />
                                <input
                                    data-testid="login-email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    autoComplete="email"
                                    className="w-full pl-10 pr-4 py-3 border border-moebus-border rounded-md text-moebus-deep focus:outline-none focus:ring-2 focus:ring-moebus-green focus:border-transparent"
                                    placeholder="info@moebus-umweltschutz.de"
                                />
                            </div>
                        </div>
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-moebus-deep mb-2">Passwort</label>
                            <div className="relative">
                                <Lock
                                    size={16}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                />
                                <input
                                    data-testid="login-password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    autoComplete="current-password"
                                    className="w-full pl-10 pr-4 py-3 border border-moebus-border rounded-md text-moebus-deep focus:outline-none focus:ring-2 focus:ring-moebus-green focus:border-transparent"
                                    placeholder="••••••••"
                                />
                            </div>
                        </div>
                        <button
                            data-testid="login-submit"
                            type="submit"
                            disabled={loading}
                            className="w-full inline-flex items-center justify-center gap-2 bg-moebus-green hover:bg-emerald-700 disabled:bg-emerald-700/60 text-white font-medium px-5 py-3 rounded-md transition-colors"
                        >
                            {loading ? (
                                <>
                                    <Loader2 size={16} className="animate-spin" /> Anmelden…
                                </>
                            ) : (
                                <>
                                    Anmelden <ArrowRight size={16} />
                                </>
                            )}
                        </button>
                    </form>

                    <Link
                        to="/"
                        className="mt-6 block text-center text-sm text-slate-400 hover:text-white transition-colors"
                    >
                        ← Zur Karriere-Seite
                    </Link>
                </div>
            </div>
        </div>
    );
}
